import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux';
import { setIsLoading, setToastMessage } from "../../reducers/common.reducer";
import { getAgentService, getAgentLoginUserService } from '../../services/agent.service';
import { FiSearch } from 'react-icons/fi'; 
import TransactionModal from './components/TransactionModal';

const AgentRemainList = () => {
    const [remainData, setRemainData] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [transaction, setTransaction] = useState({});
    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState('remainAsc');
    const [filterType, setFilterType] = useState('all');
    const [isFetching, setIsFetching] = useState(false);
    
    const dispatch = useDispatch();

    const getRemainData = async (signal) => {
        setIsFetching(true);
        dispatch(setIsLoading(true));
        try {
            let storedUser = JSON.parse(localStorage.getItem("USER"));
            const service = storedUser?.username === 'admin' ? getAgentService : getAgentLoginUserService;
            const result = await service(signal);

            if (result.data) {
                let filterData = result.data.filter(item => Number(item.remain) !== 0);
                filterData = filterData.sort((a, b) => a.remain - b.remain);
                setRemainData(filterData);
            }
        } catch (error) {
            dispatch(setToastMessage({ message: error.message, type: "error" }));
        } finally {
            dispatch(setIsLoading(false));
            setIsFetching(false);
        }
    };

    const displayData = useMemo(() => {
        let data = [...remainData];
        if (search.trim()) {
            const q = search.toLowerCase();
            data = data.filter(agent => agent.agentName?.toLowerCase().includes(q));
        }
        if (filterType === 'positive') {
            data = data.filter(agent => agent.remain > 0);
        } else if (filterType === 'negative') {
            data = data.filter(agent => agent.remain < 0);
        }
        if (sortBy === 'remainAsc') {
            data = data.sort((a, b) => a.remain - b.remain);
        } else if (sortBy === 'remainDesc') {
            data = data.sort((a, b) => b.remain - a.remain);
        } else if (sortBy === 'nameAsc') {
            data = data.sort((a, b) => (a.agentName || '').localeCompare(b.agentName || ''));
        }
        return data;
    }, [remainData, search, sortBy, filterType]);

    useEffect(() => {
        const controller = new AbortController();
        getRemainData(controller.signal);
        return () => controller.abort();
    }, []);

    const handleClick = (item) => {
        setTransaction(item);
        setIsOpen(true);
    };


    return (
        <div className="min-h-[72vh] bg-[#F1F5F9] p-2 md:p-4 font-sans">
            <div className="max-w-4xl mx-auto">

                {/* Minimalist Search */}
                <div className="sticky top-0 z-10 bg-[#F1F5F9]/80 backdrop-blur supports-[backdrop-filter]:bg-[#F1F5F9]/60 rounded-md mb-2">
                    <div className="relative">
                        <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Search by agent name..."
                            className="w-full pl-12 pr-4 py-3 bg-white border-none rounded-md shadow-sm focus:ring-2 focus:ring-blue-500/20 outline-none transition-all text-sm placeholder:text-slate-400"
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <div className="mt-2 flex flex-col md:flex-row gap-2 md:items-center md:justify-between">
                        <div className="inline-flex rounded-md bg-white p-1 shadow-sm">
                            <button
                                className={`px-3 py-1.5 text-xs font-medium rounded-md ${filterType === 'all' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'}`}
                                onClick={() => setFilterType('all')}
                            >
                                All
                            </button>
                            <button
                                className={`px-3 py-1.5 text-xs font-medium rounded-md ${filterType === 'positive' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'}`}
                                onClick={() => setFilterType('positive')}
                            >
                                Positive
                            </button>
                            <button
                                className={`px-3 py-1.5 text-xs font-medium rounded-md ${filterType === 'negative' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'}`}
                                onClick={() => setFilterType('negative')}
                            >
                                Negative
                            </button>
                        </div>
                        <div className="inline-flex rounded-md bg-white p-1 shadow-sm">
                            <button
                                className={`px-3 py-1.5 text-xs font-medium rounded-md ${sortBy === 'remainAsc' ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50'}`}
                                onClick={() => setSortBy('remainAsc')}
                            >
                                Remain ↑
                            </button>
                            <button
                                className={`px-3 py-1.5 text-xs font-medium rounded-md ${sortBy === 'remainDesc' ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50'}`}
                                onClick={() => setSortBy('remainDesc')}
                            >
                                Remain ↓
                            </button>
                            <button
                                className={`px-3 py-1.5 text-xs font-medium rounded-md ${sortBy === 'nameAsc' ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50'}`}
                                onClick={() => setSortBy('nameAsc')}
                            >
                                Name A–Z
                            </button>
                        </div>
                    </div>
                </div>

                {/* Content Area */}
                <div className="space-y-1 h-[68vh] overflow-y-auto pr-1 custom-scrollbar">
                    {isFetching && remainData.length === 0 ? (
                        Array.from({ length: 6 }).map((_, idx) => (
                            <div
                                key={idx}
                                className="animate-pulse flex items-center justify-between p-3 bg-white rounded-xl border border-transparent"
                            >
                                <div className="flex-1">
                                    <div className="h-3 w-40 bg-slate-200 rounded mb-2"></div>
                                    <div className="h-2 w-24 bg-slate-100 rounded"></div>
                                </div>
                                <div className="text-right">
                                    <div className="h-4 w-16 bg-slate-200 rounded mb-1 ml-auto"></div>
                                    <div className="h-2 w-20 bg-slate-100 rounded ml-auto"></div>
                                </div>
                            </div>
                        ))
                    ) : displayData.length > 0 ? (
                        displayData.map((item) => (
                            <div 
                                key={item.agentName} 
                                onClick={() => handleClick(item)}
                                className="group flex items-center justify-between px-3 py-2 bg-white rounded-md border border-transparent hover:border-blue-100 hover:shadow-md active:scale-[0.98] cursor-pointer transition-all duration-200"
                            >
                                <div className="flex items-center gap-2">
                                    <div className={`h-2 w-2 rounded-full ${Number(item.remain) >= 0 ? 'bg-green' : 'bg-red'}`}></div>
                                    <div className="flex flex-col gap-1">
                                        <span className="text-base font-bold text-slate-800 group-hover:text-blue-600 transition-colors">
                                        {item.agentName}
                                        </span>
                                        <span className="text-[10px] text-slate-400 font-medium">
                                            {item.finalRemainDate?.value 
                                                ? new Date(item.finalRemainDate.value).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }) 
                                                : 'No date recorded'}
                                        </span>
                                    </div>
                                </div>

                                <div className="text-right">
                                    <div className={`text-base font-bold font-mono tracking-tighter ${Number(item.remain) > 0 ? 'text-green' : 'text-red'}`}>
                                        {Number(Math.round(item.remain)).toLocaleString()}
                                    </div>
                                    <div className="text-xs uppercase tracking-widest font-bold text-slate-300">Remaining</div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-12 bg-white/50 rounded-md border border-dashed border-slate-200">
                            <p className="text-slate-400 font-medium">No agents match your search</p>
                        </div>
                    )}
                </div>
            </div>

            <TransactionModal
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                transaction={transaction}
            />
        </div>
    );
}

export default AgentRemainList;
